import React, { useState, useMemo, useCallback } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import {
  MdAssignment, MdLock, MdLockOpen, MdFilterList, MdEdit, MdEditOff, MdSave,
  MdCheckCircle, MdTune, MdAdd, MdDelete, MdSchool,
} from 'react-icons/md';
import api from '../../ui/api';
import { useAuth } from '../auth';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import PageShell, { Button } from '../../ui/PageShell';
import { EmptyState } from '../../ui/Card';
import Table from '../../ui/Table';
import Modal, { ConfirmModal } from '../../ui/Modal';
import { FormGrid, Field, Input, Select } from '../../ui/Form';
import SearchableSelect from '../../ui/SearchableSelect';
import { sectionOptions } from '../../ui/labels';
import HelpButton from '../help/HelpButton';
import EmptyGuide from '../help/EmptyGuide';
import './exams.css';

const SEMESTERS = ['فصل أول', 'فصل ثاني'];
const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyExam = {
  exam_type_id: '', title: '', grade_id: '', section_id: '', subject_id: '',
  date: todayStr(), total_marks: '', weight: '', semester: 'فصل أول',
};

/**
 * Subjects that may be examined in one section — mirrors the API's
 * teachingRelation() so the dropdown never offers what the server will
 * then refuse.
 *
 *   office  : the subjects assigned there; a homeroom section (grades
 *             1–6) is open to every subject.
 *   teacher : only what THEY teach there — their own assignments, plus,
 *             in the section they are homeroom teacher of, every subject
 *             no specialist holds.
 */
function subjectIdsFor(section, { assignments, subjects, teacherId }) {
  if (!section) return new Set();
  const active = assignments.filter((a) => a.section_id === section.id && a.status === 'active');
  if (teacherId == null) {
    if (section.homeroom_teacher_id) return new Set(subjects.map((s) => s.id));
    return new Set(active.map((a) => a.subject_id));
  }
  const ids = new Set(active.filter((a) => a.teacher_id === teacherId).map((a) => a.subject_id));
  if (section.homeroom_teacher_id === teacherId) {
    const taken = new Set(active.filter((a) => a.teacher_id !== teacherId).map((a) => a.subject_id));
    subjects.forEach((sub) => { if (!taken.has(sub.id)) ids.add(sub.id); });
  }
  return ids;
}

/**
 * Exams console.
 *
 * Two design decisions from the school drive this screen:
 *
 * 1. An exam's KIND comes from `exam_types`, a school-wide list the
 *    office manages — not free text. So "مذاكرة أولى" is the same thing
 *    in every section, and a transcript groups on (subject, kind) no
 *    matter how many times a pupil transfers.
 *
 * 2. The teacher is never picked by hand. The server resolves it: the
 *    subject's own teacher if one is assigned, otherwise the section's
 *    homeroom teacher (معلم الصف) — which is how grades 1–6 work, where
 *    one teacher takes nearly every subject.
 */
export default function Exams() {
  const toast = useToastWithSound();
  const { isOffice, isAdmin, isTeacher, user } = useAuth();
  // null for office accounts: subjectIdsFor() then applies the office rule.
  const teacherId = isTeacher ? user?.person_id : null;

  const { data, loading, error, reload } = useData(async () => {
    const [exams, sections, subjects, grades, assignments, examTypes] = await Promise.all([
      api.exams(), api.sections(), api.subjects(), api.grades(),
      api.assignments(), api.examTypes(),
    ]);
    return { exams, sections, subjects, grades, assignments, examTypes };
  }, []);

  const [showFilters, setShowFilters] = useState(false);
  const [filterSection, setFilterSection] = useState('');
  const [filterSubject, setFilterSubject] = useState('');

  const [editing, setEditing] = useState(null); // exam object or 'new'
  const [marksFor, setMarksFor] = useState(null);
  const [managingTypes, setManagingTypes] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null); // {exam, mode}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  // For a teacher the API already returns only their sections; the
  // grade and subject lists are narrowed here to match, so every
  // dropdown on this page shows their classes and nothing else.
  const sections = data?.sections || [];
  const assignments = data?.assignments || [];
  const allSubjects = data?.subjects || [];
  const subjects = useMemo(() => {
    if (!isTeacher) return allSubjects;
    const ids = new Set();
    sections.forEach((sec) => subjectIdsFor(sec, { assignments, subjects: allSubjects, teacherId })
      .forEach((id) => ids.add(id)));
    return allSubjects.filter((s) => ids.has(s.id));
  }, [isTeacher, allSubjects, sections, assignments, teacherId]);
  const grades = useMemo(() => {
    const all = data?.grades || [];
    if (!isTeacher) return all;
    const ids = new Set(sections.map((sec) => sec.grade_id));
    return all.filter((g) => ids.has(g.id));
  }, [data, isTeacher, sections]);
  const examTypes = data?.examTypes || [];

  const rows = useMemo(() => {
    let list = data?.exams || [];
    if (filterSection) list = list.filter((e) => e.section_id === Number(filterSection));
    if (filterSubject) list = list.filter((e) => e.subject_id === Number(filterSubject));
    return list.map((e) => ({
      ...e,
      kind: e.exam_type_title || e.type || '—',
      subject_title: e.subject_title || '—',
      // The full label is what search, filtering and CSV export see —
      // "أولى م" alone names 12 different sections in this school. The
      // cell renderer splits it over two lines using the parts below.
      section_title: e.section_label || e.section_title || '—',
      section_name: e.section_name || e.section_title || '',
      teacher_name: e.teacher_name || '—',
      status_label: e.is_published ? 'منشور' : 'مسودة',
    }));
  }, [data, filterSection, filterSubject]);

  const stats = useMemo(() => {
    const total = rows.length;
    const published = rows.filter((r) => r.is_published).length;
    return { total, published, draft: total - published };
  }, [rows]);

  const doPublishToggle = useCallback(async () => {
    const { exam, mode } = publishTarget;
    setBusy(true);
    try {
      if (mode === 'publish') {
        await api.publishExam(exam.id);
        toast.success('تم نشر العلامات. لن يتمكن المعلم من التعديل الآن.');
      } else {
        await api.unpublishExam(exam.id);
        toast.success('تم إلغاء النشر. يمكن للمعلم التعديل الآن.');
      }
      setPublishTarget(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  }, [publishTarget, reload]);

  const doDelete = useCallback(async () => {
    setBusy(true);
    try {
      await api.deleteExam(deleteTarget.id);
      toast.success('تم حذف الامتحان');
      setDeleteTarget(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  }, [deleteTarget, reload]);

  const clearFilters = () => { setFilterSection(''); setFilterSubject(''); };
  const filtersOn = !!(filterSection || filterSubject);

  return (
    <PageShell
      title="الامتحانات"
      description={`إنشاء الامتحانات ورصد العلامات ونشرها (${stats.total} امتحان)`}
      actions={
        <>
          {isAdmin && (
            <Button variant="secondary" onClick={() => setManagingTypes(true)}>
              <MdTune size={16} /> أنواع الامتحانات
            </Button>
          )}
          <HelpButton guides={['exam-create', 'exam-marks']} />
          <Button onClick={() => setEditing('new')} data-guide="exams-add">
            <MdAdd size={16} /> امتحان جديد
          </Button>
        </>
      }
    >
      {error && (
        <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>
      )}

      <div className="ex-stats">
        {[
          { key: 'total', label: 'إجمالي الامتحانات', value: stats.total, Icon: MdAssignment },
          { key: 'published', label: 'منشورة', value: stats.published, Icon: MdLock },
          { key: 'draft', label: 'مسودة', value: stats.draft, Icon: MdLockOpen },
        ].map((s) => (
          <div key={s.key} className={`ex-stat is-${s.key}`}>
            <span className="ex-stat-icon"><s.Icon size={24} /></span>
            <div className="ex-stat-text">
              <span className="ex-stat-value">{s.value}</span>
              <span className="ex-stat-label">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="ex-filter-card">
        <button type="button" className="ex-filter-toggle" onClick={() => setShowFilters((v) => !v)}>
          <MdFilterList size={18} />
          {showFilters ? 'إخفاء الفلاتر' : 'عرض الفلاتر'}
          {filtersOn && <span className="ex-filter-dot" title="فلاتر مفعّلة" />}
        </button>

        {showFilters && (
          <div className="ex-filters">
            <Field label="الشعبة">
              <Select
                value={filterSection}
                onChange={(e) => setFilterSection(e.target.value)}
                placeholder="جميع الشعب"
                options={sectionOptions(sections)}
              />
            </Field>
            <Field label="المادة">
              <Select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                placeholder="جميع المواد"
                options={subjects.map((s) => ({ value: s.id, label: s.title }))}
              />
            </Field>
            <div className="ex-filter-actions">
              <Button variant="secondary" onClick={clearFilters} disabled={!filtersOn}>مسح الفلاتر</Button>
            </div>
          </div>
        )}
      </div>

      <Table
        guide="exams-table"
        keyField="id"
        rows={rows}
        loading={loading}
        emptyText={<EmptyGuide text="لا توجد امتحانات بعد" guide="exam-create" />}
        searchPlaceholder="ابحث في الامتحانات..."
        exportName="الامتحانات"
        renderActions={(r) => (
          <div className="ex-actions">
            {/* The one action that keeps a text label: it is the row's
                primary action and an icon alone would not say "marks". */}
            <button type="button" className="ui-btn-mini" onClick={() => setMarksFor(r)} title="عرض ورصد العلامات" data-guide="exam-marks">
              العلامات
            </button>
            {!r.is_published && (
              <>
                <button type="button" className="ui-btn-mini is-icon-only" onClick={() => setEditing(r)} title="تعديل الامتحان">
                  <MdEdit size={15} />
                </button>
                <button
                  type="button"
                  className="ui-btn-mini ui-btn-mini-primary is-icon-only"
                  onClick={() => setPublishTarget({ exam: r, mode: 'publish' })}
                  data-guide="exam-publish"
                  title="نشر العلامات — سيُقفل التعديل على المعلم"
                >
                  <MdLock size={15} />
                </button>
              </>
            )}
            {/* Unpublishing reopens marks, so it is office-only. */}
            {!!r.is_published && isOffice && (
              <button
                type="button"
                className="ui-btn-mini ui-btn-mini-warning is-icon-only"
                onClick={() => setPublishTarget({ exam: r, mode: 'unpublish' })}
                title="إلغاء النشر — السماح للمعلم بالتعديل"
              >
                <MdLockOpen size={15} />
              </button>
            )}
            {isOffice && !r.is_published && (
              <button
                type="button"
                className="ui-btn-mini ui-btn-mini-danger is-icon-only"
                onClick={() => setDeleteTarget(r)}
                title="حذف الامتحان (يُرفض إن كانت فيه علامات)"
              >
                <MdDelete size={15} />
              </button>
            )}
          </div>
        )}
        columns={[
          {
            key: 'kind', label: 'الامتحان', width: 2, minWidth: 130,
            exportValue: (r) => `${r.kind}${r.title ? ` — ${r.title}` : ''}`,
            render: (r) => (
              <div className="ex-title-cell">
                <strong>{r.kind}</strong>
                {r.title && <span className="ex-title-extra">{r.title}</span>}
                <div className="ex-title-badges">
                  <span className="ui-badge ui-badge-neutral">{r.semester}</span>
                </div>
              </div>
            ),
          },
          { key: 'subject_title', label: 'المادة' },
          {
            // Two lines, not a pill: the full label is ~25 characters,
            // and a nowrap badge forced the table 390px past its
            // container. Grade on top, section+batch beneath.
            key: 'section_title', label: 'الشعبة', width: 1, minWidth: 110,
            render: (r) => (
              <span className="ex-section-cell">
                <span className="ex-section-grade">{r.grade_title ? `الصف ${r.grade_title}` : ''}</span>
                <span className="ex-section-name">
                  {r.section_name || r.section_title}
                  {r.batch_short ? ` (${r.batch_short})` : ''}
                </span>
              </span>
            ),
          },
          { key: 'teacher_name', label: 'المعلم' },
          { key: 'date', label: 'التاريخ', nowrap: true, filterable: false },
          {
            key: 'total_marks', label: 'العلامة', nowrap: true, align: 'center', filterable: false,
            render: (r) => <strong className="ex-marks">{r.total_marks}</strong>,
          },
          {
            key: 'weight', label: 'الوزن', nowrap: true, align: 'center', filterable: false,
            exportValue: (r) => (r.weight ? `${r.weight}%` : ''),
            render: (r) => (r.weight ? `${r.weight}%` : '—'),
          },
          {
            key: 'status_label', label: 'الحالة', nowrap: true, align: 'center',
            render: (r) => (
              <span
                className={`ex-status-badge is-${r.is_published ? 'published' : 'draft'}`}
                title={r.is_published ? 'تم نشر العلامات' : 'العلامات غير منشورة'}
              >
                {r.is_published ? <MdLock size={14} /> : <MdLockOpen size={14} />}
                {r.status_label}
              </span>
            ),
          },
        ]}
      />

      {editing && (
        <ExamForm
          exam={editing === 'new' ? null : editing}
          grades={grades}
          sections={sections}
          subjects={subjects}
          assignments={assignments}
          teacherId={teacherId}
          examTypes={examTypes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      {marksFor && (
        <ExamResults
          exam={marksFor}
          canEditPublished={isOffice}
          onClose={() => setMarksFor(null)}
          onChanged={reload}
        />
      )}

      {managingTypes && (
        <ExamTypesModal
          types={examTypes}
          grades={grades}
          onClose={() => setManagingTypes(false)}
          onChanged={reload}
        />
      )}

      <ConfirmModal
        open={!!publishTarget}
        title={publishTarget?.mode === 'publish' ? 'نشر العلامات' : 'إلغاء النشر'}
        tone={publishTarget?.mode === 'publish' ? 'primary' : 'warning'}
        confirmText={publishTarget?.mode === 'publish' ? 'نشر' : 'إلغاء النشر'}
        busy={busy}
        message={publishTarget?.mode === 'publish'
          ? `بعد نشر «${publishTarget?.exam?.kind}» لن يتمكن المعلم من تعديل العلامات أو حذفها، وستظهر العلامات للطلاب. هل تريد المتابعة؟`
          : `سيتمكن المعلمون من تعديل علامات «${publishTarget?.exam?.kind}» مرة أخرى، وستُخفى عن الطلاب. هل تريد المتابعة؟`}
        onCancel={() => setPublishTarget(null)}
        onConfirm={doPublishToggle}
      />

      <ConfirmModal
        open={!!deleteTarget}
        title="حذف الامتحان"
        message={`سيتم حذف «${deleteTarget?.kind}». لا يمكن حذف امتحان تم رصد علامات فيه.`}
        confirmText="حذف"
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={doDelete}
      />
    </PageShell>
  );
}

/* ====================================================================
 * Exam form — create and edit
 * ================================================================== */

/**
 * The cascade grade -> section -> subject is kept from the old form: it
 * stops the office picking a subject that isn't taught in the chosen
 * section. What's gone is the old "عنوان الامتحان (المادة)" field, which
 * was a second subject picker masquerading as a title.
 */
function ExamForm({ exam, grades, sections, subjects, assignments, teacherId = null, examTypes, onClose, onSaved }) {
  const toast = useToastWithSound();
  const isEdit = !!exam;

  const [form, setForm] = useState(() => {
    if (!exam) return emptyExam;
    const section = sections.find((s) => s.id === exam.section_id);
    return {
      exam_type_id: exam.exam_type_id || '',
      title: exam.title || '',
      grade_id: section?.grade_id || '',
      section_id: exam.section_id || '',
      subject_id: exam.subject_id || '',
      date: exam.date || todayStr(),
      total_marks: exam.total_marks ?? '',
      weight: exam.weight ?? '',
      semester: exam.semester || 'فصل أول',
    };
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const dirty = useDirtyForm(form);

  const activeSections = useMemo(
    () => sections.filter((s) => s.status === 'active' || s.id === form.section_id),
    [sections, form.section_id]
  );
  const gradeSections = useMemo(
    () => (form.grade_id ? activeSections.filter((s) => s.grade_id === Number(form.grade_id)) : activeSections),
    [activeSections, form.grade_id]
  );

  const selectedSection = sections.find((s) => s.id === Number(form.section_id));

  /**
   * Which subjects can this section be examined in?
   *
   * Normally: the subjects it has an assignment for. But a section with
   * a homeroom teacher (grades 1–6) is taught every subject by that one
   * teacher, so all of them are open — that is the whole point of the
   * homeroom model replacing the old "ابتدائي" catch-all subject.
   */
  const sectionSubjects = useMemo(() => {
    if (!form.section_id) return [];
    const ids = subjectIdsFor(selectedSection, { assignments, subjects, teacherId });
    return subjects.filter((s) => ids.has(s.id));
  }, [form.section_id, selectedSection, assignments, subjects, teacherId]);

  // Only types valid for this section's grade (إملاء is 1–6 only, etc.).
  const availableTypes = useMemo(() => {
    const grade = grades.find((g) => g.id === Number(form.grade_id));
    const order = grade?.sort_order ?? grade?.grade_order;
    if (order == null) return examTypes;
    return examTypes.filter((t) =>
      (t.min_grade_order == null || t.min_grade_order <= order)
      && (t.max_grade_order == null || t.max_grade_order >= order));
  }, [examTypes, grades, form.grade_id]);

  const set = (key, value) => {
    setErrors((e) => ({ ...e, [key]: '' }));
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Changing a level of the cascade clears everything below it.
      if (key === 'grade_id') { next.section_id = ''; next.subject_id = ''; }
      if (key === 'section_id') { next.subject_id = ''; }
      // Picking a kind pre-fills its defaults, but never overwrites a
      // number the office has already typed for this exam.
      if (key === 'exam_type_id') {
        const t = examTypes.find((x) => x.id === Number(value));
        if (t) {
          if (f.total_marks === '' || f.total_marks === null) next.total_marks = t.default_marks;
          if (f.weight === '' || f.weight === null) next.weight = t.default_weight;
        }
      }
      return next;
    });
  };

  const validate = () => {
    const e = {};
    if (!form.exam_type_id) e.exam_type_id = 'نوع الامتحان مطلوب';
    if (!form.section_id) e.section_id = 'الشعبة مطلوبة';
    if (!form.subject_id) e.subject_id = 'المادة مطلوبة';
    if (!form.date) e.date = 'التاريخ مطلوب';
    if (!form.total_marks || Number(form.total_marks) <= 0) e.total_marks = 'العلامة العظمى يجب أن تكون أكبر من صفر';
    if (form.weight !== '' && (Number(form.weight) < 0 || Number(form.weight) > 100)) {
      e.weight = 'الوزن يجب أن يكون بين 0 و 100';
    }
    if (!form.semester) e.semester = 'الفصل مطلوب';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return toast.error('يرجى تصحيح الأخطاء في النموذج');
    setSaving(true);
    try {
      if (isEdit) {
        // Identity (section/subject/kind) is frozen once the exam exists.
        await api.updateExam(exam.id, {
          title: form.title.trim() || null,
          date: form.date,
          total_marks: Number(form.total_marks),
          weight: form.weight === '' ? 0 : Number(form.weight),
          semester: form.semester,
        });
        toast.success('تم تحديث الامتحان');
      } else {
        // teacher_id is deliberately NOT sent: the server resolves it
        // from the assignment, or the section's homeroom teacher.
        await api.createExam({
          exam_type_id: Number(form.exam_type_id),
          title: form.title.trim() || null,
          section_id: Number(form.section_id),
          subject_id: Number(form.subject_id),
          date: form.date,
          total_marks: Number(form.total_marks),
          weight: form.weight === '' ? 0 : Number(form.weight),
          semester: form.semester,
        });
        toast.success('تم إنشاء الامتحان');
      }
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const noSubjects = form.section_id && sectionSubjects.length === 0;

  return (
    <Modal
      open
      size="lg"
      title={isEdit ? `تعديل امتحان #${exam.id}` : 'امتحان جديد'}
      onClose={onClose}
      confirmClose={dirty.isDirty}
      footer={(requestClose) => (
        <>
          <Button variant="secondary" onClick={requestClose} disabled={saving}>إلغاء</Button>
          <Button variant="success" onClick={submit} loading={saving} data-guide="modal-save">
            {!saving && <MdSave size={16} />} {isEdit ? 'حفظ التعديلات' : 'إنشاء الامتحان'}
          </Button>
        </>
      )}
    >
      <form onSubmit={submit}>
        <FormGrid columns={2}>
          <Field
            label="الصف" required={!isEdit}
            guide="exam-grade"
            hint={isEdit ? 'لا يمكن تغيير الصف بعد الإنشاء' : undefined}
          >
            <Select
              value={form.grade_id}
              onChange={(e) => set('grade_id', e.target.value)}
              placeholder="اختر الصف"
              disabled={isEdit}
              options={grades.map((g) => ({ value: g.id, label: g.title }))}
            />
          </Field>

          <Field
            label="الشعبة" required error={errors.section_id}
            guide="exam-section"
            hint={isEdit ? 'لا يمكن تغيير الشعبة بعد الإنشاء' : undefined}
          >
            <Select
              value={form.section_id}
              onChange={(e) => set('section_id', e.target.value)}
              placeholder={form.grade_id ? 'اختر الشعبة' : 'اختر الصف أولاً'}
              disabled={isEdit || !form.grade_id}
              error={!!errors.section_id}
              options={sectionOptions(gradeSections, { withCount: true })}
            />
          </Field>

          <Field
            label="المادة" required error={errors.subject_id}
            guide="exam-subject"
            hint={
              isEdit ? 'لا يمكن تغيير المادة بعد الإنشاء'
                : noSubjects ? 'لا توجد مواد لهذه الشعبة — أضف توكيلاً أو عيّن معلم صف أولاً'
                  : selectedSection?.homeroom_teacher_id
                    ? `معلم الصف: ${selectedSection.homeroom_teacher_name || '—'} — يغطي كل المواد`
                    : undefined
            }
          >
            <Select
              value={form.subject_id}
              onChange={(e) => set('subject_id', e.target.value)}
              placeholder={form.section_id ? 'اختر المادة' : 'اختر الشعبة أولاً'}
              disabled={isEdit || !form.section_id || sectionSubjects.length === 0}
              error={!!errors.subject_id}
              options={sectionSubjects.map((s) => ({ value: s.id, label: s.title }))}
            />
          </Field>

          <Field
            label="نوع الامتحان" required error={errors.exam_type_id}
            guide="exam-type"
            hint={isEdit ? 'لا يمكن تغيير النوع بعد الإنشاء' : 'من قائمة المدرسة الموحّدة — يحدّد هوية الامتحان في البيان'}
          >
            <Select
              value={form.exam_type_id}
              onChange={(e) => set('exam_type_id', e.target.value)}
              placeholder="اختر النوع"
              disabled={isEdit}
              error={!!errors.exam_type_id}
              options={availableTypes.map((t) => ({ value: t.id, label: t.title }))}
            />
          </Field>

          <Field label="الفصل" required error={errors.semester}>
            <Select
              value={form.semester}
              onChange={(e) => set('semester', e.target.value)}
              error={!!errors.semester}
              options={SEMESTERS}
            />
          </Field>

          <Field label="التاريخ" required error={errors.date}>
            <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} error={!!errors.date} />
          </Field>

          <Field label="العلامة العظمى" required error={errors.total_marks} guide="exam-total">
            <Input
              type="number" min="1" value={form.total_marks}
              onChange={(e) => set('total_marks', e.target.value)}
              error={!!errors.total_marks}
            />
          </Field>

          <Field label="الوزن %" error={errors.weight} hint="وزن الامتحان من مجموع العلامة النهائية">
            <Input
              type="number" min="0" max="100" value={form.weight}
              onChange={(e) => set('weight', e.target.value)}
              error={!!errors.weight}
            />
          </Field>

          <Field
            label="تسمية إضافية" span
            hint="اختياري — مثل «امتحان الوحدة الثالثة». النوع أعلاه هو هوية الامتحان."
          >
            <Input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="اتركه فارغاً إن لم تكن هناك تسمية خاصة"
            />
          </Field>
        </FormGrid>
      </form>
    </Modal>
  );
}

/* ====================================================================
 * Exam results — the mark-entry screen
 * ================================================================== */

/**
 * Marks for one exam, over the section's whole roster.
 *
 * The roster (not just the marked pupils) is the list, so the teacher
 * can see who is still missing. Results are keyed on (exam, student)
 * only, so they survive a transfer untouched.
 */
function ExamResults({ exam, canEditPublished, onClose, onChanged }) {
  const toast = useToastWithSound();
  const { data, loading, reload } = useData(async () => {
    const [full, results, roster] = await Promise.all([
      api.exam(exam.id),
      api.examResults(exam.id),
      api.sectionRoster(exam.section_id),
    ]);
    return { full, results, roster };
  }, [exam.id]);

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const dirty = useDirtyForm(draft);

  const full = data?.full || exam;
  const totalMarks = full.total_marks || exam.total_marks;
  const locked = full.is_published && !canEditPublished;

  const rows = useMemo(() => {
    const results = data?.results || [];
    const rosterRaw = data?.roster;
    const roster = rosterRaw?.students || rosterRaw || [];
    const byStudent = new Map(results.map((r) => [Number(r.student_id), r]));
    return roster.map((s) => {
      const sid = Number(s.student_id ?? s.id);
      const r = byStudent.get(sid);
      return {
        id: sid,
        student_id: sid,
        student_name: s.name || s.student_name || r?.student_name || `#${sid}`,
        marks_obtained: r?.marks_obtained ?? null,
        remarks: r?.remarks || '',
        result_id: r?.id || null,
        has_result: !!r,
      };
    });
  }, [data]);

  const stats = useMemo(() => {
    const marked = rows.filter((r) => r.marks_obtained !== null);
    if (marked.length === 0) {
      return { total: rows.length, marked: 0, avg: null, high: null, low: null, passed: null };
    }
    const values = marked.map((r) => r.marks_obtained);
    const passMark = totalMarks / 2;
    return {
      total: rows.length,
      marked: marked.length,
      avg: (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2),
      high: Math.max(...values),
      low: Math.min(...values),
      passed: marked.filter((r) => r.marks_obtained >= passMark).length,
    };
  }, [rows, totalMarks]);

  const setDraftValue = (sid, field, value) =>
    setDraft((d) => ({ ...d, [sid]: { ...d[sid], [field]: value } }));

  const readDraft = (row, field) => {
    const d = draft[row.student_id];
    if (d && d[field] !== undefined) return d[field];
    return field === 'marks' ? (row.marks_obtained ?? '') : row.remarks;
  };

  const validMark = (value) => {
    const n = Number(value);
    return !(value === '' || value === null || Number.isNaN(n) || n < 0 || n > totalMarks);
  };

  /** Save (or correct) one pupil's mark. */
  const saveOne = async (row) => {
    const marks = readDraft(row, 'marks');
    if (!validMark(marks)) return toast.error(`العلامة يجب أن تكون بين 0 و ${totalMarks}`);
    const remarks = readDraft(row, 'remarks') || null;
    setSaving(true);
    try {
      if (row.has_result) {
        await api.updateExamResult(exam.id, row.result_id, { marks_obtained: Number(marks), remarks });
        toast.success(`تم تحديث درجة ${row.student_name}`);
      } else {
        await api.createExamResult(exam.id, { student_id: row.student_id, marks_obtained: Number(marks), remarks });
        toast.success(`تم حفظ درجة ${row.student_name}`);
      }
      setDraft((d) => { const n = { ...d }; delete n[row.student_id]; return n; });
      reload();
      onChanged();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  /**
   * "رفع العلامات" — send every newly typed mark at once. The server
   * skips anyone who already has one, so this can never overwrite a
   * saved mark by accident; correcting one is the per-row save above.
   */
  const saveAll = async () => {
    const payload = [];
    for (const row of rows) {
      if (row.has_result) continue;
      const marks = draft[row.student_id]?.marks;
      if (marks === undefined || marks === '') continue;
      if (!validMark(marks)) return toast.error(`درجة ${row.student_name} غير صحيحة`);
      payload.push({
        student_id: row.student_id,
        marks_obtained: Number(marks),
        remarks: draft[row.student_id]?.remarks || null,
      });
    }
    if (payload.length === 0) return toast.error('لا توجد درجات جديدة لحفظها');

    setSaving(true);
    try {
      const res = await api.bulkCreateExamResults(exam.id, payload);
      toast.success(`تم حفظ ${res.saved} درجة`);
      if (res.failed > 0) toast.warning(`تعذّر حفظ ${res.failed} درجة`);
      setDraft({});
      reload();
      onChanged();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const pendingCount = rows.filter(
    (r) => !r.has_result && draft[r.student_id]?.marks !== undefined && draft[r.student_id]?.marks !== ''
  ).length;

  return (
    <Modal
      open
      size="lg"
      title={`العلامات — ${exam.kind || exam.exam_type_title}`}
      onClose={onClose}
      confirmClose={dirty.isDirty}
      footer={(requestClose) => <Button variant="secondary" onClick={requestClose} data-guide="modal-close">إغلاق</Button>}
    >
      {/* !! matters: is_published is SQLite's 0/1 integer, and a bare
          `0 && <jsx>` renders a literal "0" on the page. */}
      {!!full.is_published && (
        <div className={`ex-publish-banner ${canEditPublished ? 'is-office' : 'is-teacher'}`}>
          <MdLock size={22} className="ex-publish-icon" />
          <div>
            <strong>العلامات منشورة ومقفلة</strong>
            <p>
              {canEditPublished
                ? 'بصفتك من الإدارة يمكنك التعديل رغم النشر.'
                : 'لا يمكن تعديل أو حذف العلامات بعد النشر.'}
            </p>
            {full.published_by_name && full.published_at && (
              <small>
                نُشرت بواسطة {full.published_by_name} — {new Date(full.published_at).toLocaleDateString('ar-EG')}
              </small>
            )}
          </div>
        </div>
      )}

      <div className="ex-info-card">
        {[
          ['المادة', full.subject_title || exam.subject_title],
          ['الشعبة', full.section_title || exam.section_title],
          ['المعلم', full.teacher_name || exam.teacher_name],
          ['التاريخ', full.date],
          ['العلامة العظمى', `${totalMarks} درجة`],
        ].map(([label, value]) => (
          <div key={label} className="ex-info-item">
            <span className="ex-info-label">{label}</span>
            <span className="ex-info-value">{value || '—'}</span>
          </div>
        ))}
      </div>

      <div className="ex-guidance">
        <div className="ex-guidance-head"><MdAssignment size={18} /> إرشادات رصد العلامات</div>
        <ul>
          <li><strong>تعديل العلامات:</strong> اضغط «تعديل العلامات» لبدء الإدخال أو التصحيح.</li>
          <li><strong>حفظ متعدد:</strong> «رفع العلامات» يحفظ كل الدرجات الجديدة دفعة واحدة، أو احفظ درجة كل طالب على حدة.</li>
          <li><strong>هام:</strong> «رفع العلامات» يحفظ الدرجات الجديدة فقط ولا يعدّل درجة محفوظة مسبقاً. لتغيير درجة محفوظة استخدم زر الحفظ في سطر الطالب.</li>
        </ul>
      </div>

      <div className="ex-results-bar">
        <Button
          variant={editMode ? 'warning' : 'primary'}
          data-guide="marks-edit"
          onClick={() => {
            if (locked) return toast.error('لا يمكن تعديل العلامات بعد نشرها.');
            setEditMode((v) => !v);
          }}
          disabled={locked}
        >
          {editMode ? <MdEditOff size={16} /> : <MdEdit size={16} />}
          {editMode ? 'إيقاف التعديل' : 'تعديل العلامات'}
        </Button>

        {editMode && (
          <Button variant="success" onClick={saveAll} disabled={saving || pendingCount === 0} data-guide="marks-upload">
            <MdSave size={16} /> رفع العلامات {pendingCount > 0 && `(${pendingCount})`}
          </Button>
        )}
      </div>

      {loading ? (
        <p className="ex-loading">جارٍ تحميل العلامات...</p>
      ) : rows.length === 0 ? (
        <EmptyState icon={<MdSchool size={40} />} title="لا يوجد طلاب" message="لا يوجد طلاب مسجّلون في شعبة هذا الامتحان." />
      ) : (
        <Table
          guide="marks-table"
          keyField="id"
          rows={rows}
          pageSize={50}
          emptyText="لا يوجد طلاب"
          searchPlaceholder="ابحث عن طالب..."
          exportName={`علامات-${exam.kind || 'امتحان'}`}
          renderActions={editMode ? (r) => (
            <button
              type="button"
              className="ui-btn-mini ui-btn-mini-primary"
              onClick={() => saveOne(r)}
              disabled={saving || readDraft(r, 'marks') === ''}
              title="حفظ درجة هذا الطالب"
            >
              <MdSave size={15} />
            </button>
          ) : null}
          columns={[
            {
              key: 'student_name', label: 'اسم الطالب', width: 2,
              render: (r) => (
                <div className="ex-student-cell">
                  <strong>{r.student_name}</strong>
                  {r.has_result && (
                    <span className="ex-saved-badge"><MdCheckCircle size={13} /> محفوظة</span>
                  )}
                </div>
              ),
            },
            {
              key: 'marks_obtained', label: `الدرجة (من ${totalMarks})`, nowrap: true, align: 'center', filterable: false,
              exportValue: (r) => (r.marks_obtained ?? ''),
              render: (r) => (editMode ? (
                <input
                  type="number" className="ui-input ex-mark-input"
                  min="0" max={totalMarks}
                  value={readDraft(r, 'marks')}
                  onChange={(e) => setDraftValue(r.student_id, 'marks', e.target.value)}
                  placeholder="0"
                />
              ) : (
                <span className={`ex-mark ${r.marks_obtained !== null ? 'has-mark' : ''}`}>
                  {r.marks_obtained !== null ? r.marks_obtained : '—'}
                </span>
              )),
            },
            {
              key: 'percentage', label: 'النسبة', nowrap: true, align: 'center', filterable: false, sortable: false,
              // Recomputed from the draft so the percentage moves as the
              // teacher types, before anything is saved.
              exportValue: (r) => (r.marks_obtained !== null
                ? `${((r.marks_obtained / totalMarks) * 100).toFixed(1)}%` : ''),
              render: (r) => {
                const raw = readDraft(r, 'marks');
                if (raw === '' || raw === null || Number.isNaN(Number(raw))) return '—';
                const pct = (Number(raw) / totalMarks) * 100;
                const tone = pct >= 75 ? 'good' : pct >= 50 ? 'ok' : 'bad';
                return <span className={`ex-pct is-${tone}`}>{pct.toFixed(1)}%</span>;
              },
            },
            {
              key: 'remarks', label: 'ملاحظات', filterable: false,
              render: (r) => (editMode ? (
                <input
                  type="text" className="ui-input ex-remark-input"
                  value={readDraft(r, 'remarks')}
                  onChange={(e) => setDraftValue(r.student_id, 'remarks', e.target.value)}
                  placeholder="ملاحظة"
                />
              ) : (r.remarks || '—')),
            },
          ]}
        />
      )}

      {stats.marked > 0 && (
        <div className="ex-result-stats">
          <h4>الإحصائيات</h4>
          <div className="ex-result-stats-grid">
            {[
              ['عدد الطلاب', stats.total],
              ['تم رصدهم', `${stats.marked} / ${stats.total}`],
              ['المتوسط', stats.avg],
              ['أعلى درجة', stats.high],
              ['أدنى درجة', stats.low],
              ['الناجحون', `${stats.passed} / ${stats.marked}`],
            ].map(([label, value]) => (
              <div key={label} className="ex-result-stat">
                <span className="ex-result-stat-label">{label}</span>
                <span className="ex-result-stat-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ====================================================================
 * Exam types — the school-wide list, managed by the admin
 * ================================================================== */

const emptyType = {
  title: '', sort_order: '', default_marks: 100, default_weight: 0,
  min_grade_order: '', max_grade_order: '',
};

/**
 * The list that makes an exam's kind mean the same thing school-wide.
 * A kind that real exams already use is deactivated rather than deleted,
 * so old exams keep their meaning — the server enforces that; this
 * screen just reports what it decided.
 */
function ExamTypesModal({ types, grades, onClose, onChanged }) {
  const toast = useToastWithSound();
  const [form, setForm] = useState(emptyType);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = useDirtyForm(form);

  // Grades in class order, so the "من صف/إلى صف" lists read top-to-
  // bottom the way a teacher thinks (الأول, الثاني, الثالث...).
  const gradesInOrder = useMemo(
    () => [...(grades || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [grades]
  );
  const gradeOptions = gradesInOrder.map((g) => ({ value: g.sort_order, label: g.title }));
  const gradeNameByOrder = useMemo(
    () => new Map(gradesInOrder.map((g) => [g.sort_order, g.title])),
    [gradesInOrder]
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setDirect = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async (ev) => {
    ev.preventDefault();
    if (!form.title.trim()) return toast.error('اسم النوع مطلوب');
    if (!form.default_marks || Number(form.default_marks) <= 0) {
      return toast.error('العلامة الافتراضية يجب أن تكون أكبر من صفر');
    }
    setSaving(true);
    try {
      await api.createExamType({
        title: form.title.trim(),
        sort_order: form.sort_order === '' ? 0 : Number(form.sort_order),
        default_marks: Number(form.default_marks),
        default_weight: form.default_weight === '' ? 0 : Number(form.default_weight),
        min_grade_order: form.min_grade_order === '' ? null : Number(form.min_grade_order),
        max_grade_order: form.max_grade_order === '' ? null : Number(form.max_grade_order),
      });
      toast.success('تمت إضافة النوع');
      setForm(emptyType);
      onChanged();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (t) => {
    try {
      await api.updateExamType(t.id, { status: t.status === 'active' ? 'inactive' : 'active' });
      toast.success(t.status === 'active' ? 'تم تعطيل النوع' : 'تم تفعيل النوع');
      onChanged();
    } catch (err) { toast.error(err.message); }
  };

  const doRemove = async () => {
    setBusy(true);
    try {
      const res = await api.deleteExamType(removing.id);
      toast.success(res.deactivated
        ? 'النوع مستخدم في امتحانات قائمة — تم تعطيله بدل حذفه'
        : 'تم حذف النوع');
      setRemoving(null);
      onChanged();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Modal
        open
        size="lg"
        title="أنواع الامتحانات"
        onClose={onClose}
        confirmClose={dirty.isDirty}
        footer={(requestClose) => <Button variant="secondary" onClick={requestClose}>إغلاق</Button>}
      >
        <div className="ui-notice ui-notice-info">
          <div className="ui-notice-body">
            هذه قائمة المدرسة الموحّدة. النوع نفسه يُستخدم في كل الشعب، ولهذا
            يستطيع البيان جمع علامات المادة الواحدة عبر الصفوف حتى لو انتقل الطالب.
          </div>
        </div>

        <form onSubmit={add} className="ex-type-form">
          <FormGrid columns={3}>
            <Field label="اسم النوع" required>
              <Input value={form.title} onChange={set('title')} placeholder="مثل: سبر معلومات 3" />
            </Field>
            <Field label="العلامة الافتراضية" required>
              <Input type="number" min="1" value={form.default_marks} onChange={set('default_marks')} />
            </Field>
            <Field label="الوزن الافتراضي %">
              <Input type="number" min="0" max="100" value={form.default_weight} onChange={set('default_weight')} />
            </Field>
            <Field label="الترتيب" hint="يحدّد موضعه في القائمة">
              <Input type="number" value={form.sort_order} onChange={set('sort_order')} placeholder="0" />
            </Field>
            <Field label="من صف" hint="اتركه فارغاً لكل الصفوف">
              <SearchableSelect
                value={form.min_grade_order}
                onChange={setDirect('min_grade_order')}
                placeholder="كل الصفوف"
                options={gradeOptions}
              />
            </Field>
            <Field label="إلى صف" hint="اتركه فارغاً لكل الصفوف">
              <SearchableSelect
                value={form.max_grade_order}
                onChange={setDirect('max_grade_order')}
                placeholder="كل الصفوف"
                options={gradeOptions}
              />
            </Field>
          </FormGrid>
          <div className="ex-type-form-actions">
            <Button variant="success" onClick={add} loading={saving}>
              {!saving && <MdAdd size={16} />} إضافة نوع
            </Button>
          </div>
        </form>

        <ul className="ex-type-list">
          {types.map((t) => (
            <li key={t.id} className={`ex-type-row ${t.status !== 'active' ? 'is-inactive' : ''}`}>
              <span className="ex-type-title">{t.title}</span>
              <span className="ex-type-meta">
                {t.default_marks} درجة
                {t.default_weight > 0 && ` · وزن ${t.default_weight}%`}
                {(t.min_grade_order != null || t.max_grade_order != null)
                  ? ` · من ${gradeNameByOrder.get(t.min_grade_order) ?? gradesInOrder[0]?.title ?? '؟'}`
                    + ` إلى ${gradeNameByOrder.get(t.max_grade_order) ?? gradesInOrder[gradesInOrder.length - 1]?.title ?? '؟'}`
                  : ''}
              </span>
              <span className="ex-type-buttons">
                <button type="button" className="ui-btn-mini" onClick={() => toggleStatus(t)}>
                  {t.status === 'active' ? 'تعطيل' : 'تفعيل'}
                </button>
                <button type="button" className="ui-btn-mini ui-btn-mini-danger" onClick={() => setRemoving(t)}>
                  <MdDelete size={15} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      </Modal>

      <ConfirmModal
        open={!!removing}
        title="حذف نوع امتحان"
        message={`سيتم حذف «${removing?.title}». إن كانت هناك امتحانات تستخدمه فسيُعطَّل بدل حذفه حفاظاً على سجلّها.`}
        confirmText="حذف"
        busy={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={doRemove}
      />
    </>
  );
}
